import type { AvailabilitySnapshot, EpisodeManifest } from "./stage-api";
import { editionRevisionFor, resolvesEditionId } from "./edition-revisions.mjs";

export type StageSelectionMode = "current" | "replay" | "fallback";

/** Verify against availability's public selection; failed checks never replace a pinned edition. */
export async function isPastEdition(episode: EpisodeManifest, mode: StageSelectionMode,
  currentUrl: string | undefined, load: (url: string) => Promise<EpisodeManifest>): Promise<boolean> {
  if (mode !== "replay" || !currentUrl || episode.privateNewsroomPreview) return false;
  try {
    const current = await load(currentUrl);
    const currentRevision = editionRevisionFor(current), selectedRevision = editionRevisionFor(episode);
    return (currentRevision?.editionId ?? current.id) !== (selectedRevision?.editionId ?? episode.id)
      && Date.parse(currentRevision?.originalPublishedAt ?? current.publishedAt) > Date.parse(selectedRevision?.originalPublishedAt ?? episode.publishedAt);
  } catch {
    return false;
  }
}

/** Never synthesize a summary from a headline, source excerpt or opening dialogue. */
export function editionBriefFor(episode: EpisodeManifest): string | null {
  const brief = episode.editionBrief;
  if (brief?.version === "published-edition-brief-v1" && brief.episodeId === episode.id &&
    typeof brief.text === "string" && brief.text.trim().length > 0) return brief.text;
  const preview = episode.privateNewsroomPreview === true ? episode.privateEditionBrief : undefined;
  return preview?.version === "private-edition-brief-v1" && preview.episodeId === episode.id &&
    typeof preview.text === "string" && preview.text.trim().length > 0 ? preview.text : null;
}

export function stagePresentation(episode: EpisodeManifest, mode: StageSelectionMode,
  contentMode?: AvailabilitySnapshot["contentMode"]) {
  const replay = mode === "replay" || (mode === "current" && contentMode === "encore")
    || episode.newsCoverage?.presentation?.kind === "replay";
  const explainer = episode.explainerCoverage;
  const recent = episode.newsCoverage?.presentation?.kind === "recent-news";
  const label = mode === "fallback" ? "STUDIO STANDBY" : replay
    ? (explainer ? "EXPLAINER REPLAY" : "REPLAY") : explainer ? "EXPLAINER" : recent ? "RECENT NEWS" : null;
  const revision = editionRevisionFor(episode);
  const published = timestampDate(revision?.originalPublishedAt) ?? timestampDate(episode.publishedAt);
  const verified = timestampDate(explainer?.verifiedAtUtc);
  const development = calendarDate(episode.newsCoverage?.presentation?.developmentDate ?? episode.newsCoverage?.developmentDate);
  const details = mode === "fallback" ? "A prepared studio performance, not a new news edition."
    : [replay ? `Original publication: ${published ?? "date unavailable"}.` : null,
      explainer ? `Explainer verified: ${verified ?? "date unavailable"}. Not a new news development.` : null,
      recent ? `Development date: ${development ?? "date unavailable"}.` : null].filter(Boolean).join(" ");
  return { label, details, playLabel: mode === "fallback" ? "Play studio preview" : replay ? "Play edition"
    : explainer ? "Play explainer" : recent ? "Play recent news" : "Play today's edition" };
}

function timestampDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.valueOf()) || date.valueOf() <= 0) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/Denver" }).format(date);
}

function calendarDate(value?: string): string | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.valueOf()) || date.toISOString().slice(0, 10) !== value) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(date);
}

/** Pinned editions take precedence over shares/current. Failure is explicit, never a different edition. */
export async function selectStageEpisode(query: URLSearchParams, currentUrl: string | undefined,
  load: (url: string) => Promise<EpisodeManifest>): Promise<{
    episode: EpisodeManifest | null; mode: StageSelectionMode; error: string | null;
  }> {
  const id = query.get("episode");
  const share = query.get("share");
  const pinned = id !== null || share !== null;
  try {
    if (id !== null) {
      if (!/^[A-Za-z0-9_-]{1,160}$/.test(id)) throw new Error("Invalid episode identifier");
      const episode = await load(`/api/episode-manifests/${encodeURIComponent(id)}`);
      if (!resolvesEditionId(episode, id)) throw new Error("Episode identity mismatch");
      return { episode, mode: "replay", error: null };
    }
    if (share !== null) {
      if (!/^[A-Za-z0-9_-]{1,256}$/.test(share)) throw new Error("Invalid share identifier");
      return { episode: await load(`/api/shares/${encodeURIComponent(share)}`), mode: "replay", error: null };
    }
    return { episode: currentUrl ? await load(currentUrl) : null, mode: currentUrl ? "current" : "fallback", error: null };
  } catch {
    return { episode: null, mode: "fallback", error: pinned
      ? "The requested edition is unavailable. This is a studio fallback, not that edition. Browse Stories to choose another." : null };
  }
}
